import { Component, Input, OnInit } from '@angular/core';
import { LotPermissions } from '@app/core/permissions';
import { ProjectReleaseStagePermissions } from '@app/core/permissions/project-release-stage.permissions';
import { NavigationService } from '@app/core/services/navigation/navigation.service';
import { ToastService } from '@app/core/services/toast/toast.service';
import { ILotMappedItem, LotsLogicService } from '@app/logic/lots';
import { IReleaseStageMappedItem } from '@app/logic/release-stages/interfaces/i.release-stage.mapped';
import {
    ReleaseStagesLogicService
} from '@app/logic/release-stages/release-stages.logic.service';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import { LotSwitchReleaseStageDialogComponent } from '@app/views/lot/shared/lot-switch-release-stage-dialog/lot-switch-release-stage-dialog.component';
import {
    IProjectReleaseLotDto,
    IProjectReleaseStageDto,
    LotTypeEnumId,
    LOT_CONTRACT_TYPE_ENUM,
    LOT_JOB_STATUS_ENUM,
    LOT_TYPE_ENUM,
    MARKET_STATUS_ENUM,
    PROJECT_RELEASE_STAGE_TYPE_ENUM,
    PROJECT_RELEASE_STATUS_ENUM
} from '@classictechsolutions/hubapi-transpiled-enums';
import { ProjectReleaseStageEditLotDialogComponent } from './edit/edit-lot-dialog.component';
import { ImportLotsDialogComponent } from './import/import-lots-dialog.component';
import { ProjectReleaseStageReleaseLotsToSalesDialogComponent } from './sales/release-lots-to-sales-dialog.component';
import { LotJobStatusEnumId } from '@classictechsolutions/hubapi-transpiled-enums/build/main/lib/enums/LotJobStatusEnum';
import { take } from 'rxjs';
import { LotMigratorDialogComponent } from '@app/views/projects/release/lots/lot-migrator/lot-migrator-dialog.component';
import { HouseAndLandNationalPermissions } from '@app/core/permissions/national.permission';
import { BlockingDialogComponent } from '@app/shared/components/dialog/blocking-dialog/blocking-dialog.component';
import { MatDialogRef } from '@angular/material/dialog';

const MIGRATION_RELEASE_CANCELLED_TOOLTIP =
    'The Migrate Lots Wizard is not available when the Release Stage Status is Cancelled.';

@Component({
    selector: 'cb-manage-project-release-stage-lots-summary-tab',
    templateUrl: './release-lots-summary.component.html',
    styleUrls: ['./release-lots-summary.component.scss'],
    standalone: false
})
export class ManageProjectReleaseStageLotsSummaryTabComponent implements OnInit {
    @Input() public mappedItem: IReleaseStageMappedItem;
    @Input() public projectReleases: IProjectReleaseStageDto[];
    public selectedTabIndex = 0;
    public lots: IProjectReleaseLotDto[];
    public loaded = false;
    public MARKET_STATUS_ENUM = MARKET_STATUS_ENUM;
    public LOT_JOB_STATUS_ENUM = LOT_JOB_STATUS_ENUM;
    public LOT_CONTRACT_TYPE_ENUM = LOT_CONTRACT_TYPE_ENUM;
    public LOT_TYPE_ENUM = LOT_TYPE_ENUM;
    constructor(
        private readonly releaseStagesLogicService: ReleaseStagesLogicService,
        public readonly lotPermissions: LotPermissions,
        public readonly projectReleaseStagePermissions: ProjectReleaseStagePermissions,
        public readonly cbDialog: CbDialogService,
        private readonly navigationService: NavigationService,
        private readonly toastService: ToastService,
        private readonly lotsLogicService: LotsLogicService,
        private readonly houseAndLandNationalPermissions: HouseAndLandNationalPermissions
    ) { }

    public ngOnInit(
    ): void {
        this.loadLots();
    }

    public getMarketStatusIcon(lot: IProjectReleaseLotDto): string | null {
        switch (lot.marketStatus) {
            case MARKET_STATUS_ENUM.Sold:
                return 'monetization_on';
            case MARKET_STATUS_ENUM.NotAvailable:
                return 'block_black';
            case MARKET_STATUS_ENUM.Available:
                return 'attach_money';
            case MARKET_STATUS_ENUM.PreRelease:
                return 'info_black';
            default:
                return;
        }
    }

    public editLot(lot: IProjectReleaseLotDto) {
        this.lotsLogicService.$getMappedItem(lot.id).pipe(take(1)).subscribe(LotMappedItem => {
            this.cbDialog
                .open(ProjectReleaseStageEditLotDialogComponent, {
                    data: { mappedItem: LotMappedItem, isReadonly: false },
                    minWidth: '80%',
                })
                .afterClosed()
                .pipe(take(1)).subscribe((result: ILotMappedItem) => {
                if (result) {
                    const index = result?.id != null && this.lots.findIndex(x => x.id === result.id);
                    if (index >= 0) {
                        Object.assign(this.lots[index], result);
                    }
                }
            });
        });
    }

    public viewLotDetails(lot: IProjectReleaseLotDto) {
        this.lotsLogicService.$getMappedItem(lot.id).pipe(take(1)).subscribe(LotMappedItem => {
            this.cbDialog
                .open(ProjectReleaseStageEditLotDialogComponent, {
                    data: { mappedItem: LotMappedItem, isReadonly: true },
                    minWidth: '80%',
                })
                .afterClosed()
                .pipe(take(1)).subscribe();
        });
    }

    public viewLot(lot: IProjectReleaseLotDto): void {
        this.navigationService.navigate([`lots/${lot.id}/summary`]);
    }

    public getAmenitiesDisplay(noAmenities: number, lotType: LotTypeEnumId) {
        return this.isBlockLotType(lotType) ? '-' : noAmenities;
    }

    public isBlockLotType(value: LotTypeEnumId) {
        return value === LOT_TYPE_ENUM.ApartmentBlock || value === LOT_TYPE_ENUM.TerracedBlock;
    }

    public canReleaseLot(): boolean {
        return this.mappedItem.type === PROJECT_RELEASE_STAGE_TYPE_ENUM.HouseAndLand
            && this.lots.length > 0
            && this.mappedItem.releaseStatus !== PROJECT_RELEASE_STATUS_ENUM.Cancelled;
    }

    public canEditLot(lot: IProjectReleaseLotDto) {
        return !lot.hasPriceRevisions &&
            !lot.hasSchemesInProgress &&
            !lot.activeConceptId;
    }

    public canImportLots() {
        return this.mappedItem.releaseStatus !== PROJECT_RELEASE_STATUS_ENUM.Cancelled;
    }

    public canMakeAvailableForSale(lot: IProjectReleaseLotDto): boolean {
        return !lot.hasClientSale
            && lot.marketStatus !== MARKET_STATUS_ENUM.Available
            && (lot.activeConceptId || lot.lotContractType === LOT_CONTRACT_TYPE_ENUM.DesignAndLand)
            && !lot.hasEstimatedPricingRevisionOnAppliedConcept;
    }

    public makeAvailableForSale(lot: IProjectReleaseLotDto) {
        const ids: string[] = [lot.id.toString()];

        this.lotsLogicService.updateMarketStatus(ids, MARKET_STATUS_ENUM.Available).pipe(take(1)).subscribe(
            (results) => {
                if (results) {
                    this.toastService.saveSuccess();
                    this.loadLots();
                }
            });
    }

    public canMakeUnavailableForSale(lot: IProjectReleaseLotDto): boolean {
        return lot.marketStatus !== MARKET_STATUS_ENUM.NotAvailable && !lot.hasClientSale;
    }

    public setAsExternalSale(lot: IProjectReleaseLotDto) {
        this.cbDialog.simpleMessageDialog('Set as External Sale is Not yet Implemented');
    }

    public canSwitchReleaseStage(lot: IProjectReleaseLotDto): boolean {
        return !lot.hasClientSale && this.projectReleases?.length > 1;
    }

    public removeLotFromRelease(lot) {
        const isParent = lot.isParent;
        const message = isParent
            ? 'Are you sure you want to remove the Parent Lot and all Child Lots from this Release?'
            : 'Are you sure you want to remove this Lot from the Release?';

        const dialogHeading = isParent
            ? 'Remove Parent Lot and all Child Lots from Release'
            : 'Remove Lot from Release';

        this.cbDialog.confirm({
            dialogHeading,
            message,
            confirmed: () => {
                this.lotsLogicService.deleteLot(lot.id).pipe(take(1)).subscribe(() => {
                    this.toastService.saveSuccess();
                    this.loadLots();
                });
            }
        });
    }

    public getRemoveLotDisabledReason(lot: IProjectReleaseLotDto): string | null {
        if (this.isChild(lot)) {
            return 'Action disabled. Cancel the Parent Lot to cancel all units.';
        }

        if (!this.isCorrectJobStatus(lot)) {
            return 'Lots with Handover or Cancelled status cannot be removed.';
        }

        if (lot.hasClientSale) {
            return 'Lots with a client sale cannot be removed from Release.';
        }

        if (lot.childrenHaveClientSale) {
            return 'Cannot remove: One or more child lots have an active client sale';
        }

        return null;
    }

    public canRemoveLotFromRelease(lot: IProjectReleaseLotDto): boolean {
        if (this.isChild(lot)) {
            return false;
        }

        if (this.isCorrectJobStatus(lot)) {
            return !lot.hasClientSale && !lot.childrenHaveClientSale;
        }

        return false;
    }

    public isChild(lot: IProjectReleaseLotDto): boolean {
        return lot.buildLink != null;
    }

    public isCorrectJobStatus(lot: IProjectReleaseLotDto): boolean {
        return lot.jobStatus !== LotJobStatusEnumId.Handover && lot.jobStatus !== LotJobStatusEnumId.Cancelled;
    }

    public switchReleaseStage(lot: IProjectReleaseLotDto) {
        this.lotsLogicService.$getMappedItem(lot.id).pipe(take(1)).subscribe(lotMappedItem => {
            this.cbDialog
                .open(LotSwitchReleaseStageDialogComponent, {
                    data: { mappedItem: lotMappedItem }
                })
                .afterClosed()
                .pipe(take(1)).subscribe((result) => {
                if (result) {
                    this.loadLots();
                }
            });
        });
    }

    private loadLots = () => {
        this.loaded = false;
        this.releaseStagesLogicService.getProjectReleaseStageLots(this.mappedItem.id).pipe(take(1)).subscribe((result) => {
            this.lots = result;
            this.loaded = true;
            this.refreshReleaseState();
        });
    };

    public makeUnavailableForSale(lot: IProjectReleaseLotDto) {
        const ids: string[] = [lot.id.toString()];

        this.lotsLogicService.updateMarketStatus(ids, MARKET_STATUS_ENUM.NotAvailable).pipe(take(1)).subscribe(
            (results) => {
                if (results) {
                    this.toastService.saveSuccess();
                    this.loadLots();
                }
            });
    }

    public releaseToSalesDialog() {
        if (this.canReleaseLot()) {
            this.cbDialog
                .open(ProjectReleaseStageReleaseLotsToSalesDialogComponent, {
                    data: { projectReleaseStageId: this.mappedItem.id, projectId: this.mappedItem.projectId },
                    minWidth: '1000px',
                })
                .afterClosed()
                .pipe(take(1)).subscribe(result => {
                if (result) {
                    this.loadLots();
                }
            });
        }
    }

    public importLotsDialog() {
        this.cbDialog
            .open(ImportLotsDialogComponent, {
                data: { projectReleaseStageMappedItem: this.mappedItem },
                minWidth: '1000px',
            })
            .afterClosed()
            .pipe(take(1)).subscribe(result => {
            if (result) {
                this.loadLots();
            }
        });
    }

    private refreshReleaseState(): void {
        this.mappedItem.$reload().pipe(take(1)).subscribe();
    }

    public openMigrateLotWizard(): void {
        if (!this.canAccessMigrateLots() || this.isReleaseStageCancelled()) {
            return;
        }
        const blockingRef: MatDialogRef<BlockingDialogComponent> = this.cbDialog.block('Loading migration lots');

        this.releaseStagesLogicService
            .getProjectReleaseStageMigrationLots(this.mappedItem.id)
            .pipe(take(1))
            .subscribe({
                next: migrationLots => {
                    blockingRef.close();

                    this.cbDialog
                        .open(LotMigratorDialogComponent, {
                            data: {
                                migrationLots,
                                projectReleaseId: this.mappedItem.id,
                                allowedLotContractTypes: this.mappedItem?.allowedLotContractTypes,
                                projectReleaseStageMappedItem: this.mappedItem
                            },
                            minWidth: '100%',
                            fullWidth: true
                        })
                        .afterClosed()
                        .pipe(take(1))
                        .subscribe(result => {
                            if (result) {
                                this.loadLots();
                            }
                        });
                },
                error: () => {
                    blockingRef.close();
                }
            });
    }

    public canAccessMigrateLots(): boolean {
        return (
            this.houseAndLandNationalPermissions.canMigrateLots()
        );
    }

    public isReleaseStageCancelled(): boolean {
        return this.mappedItem?.releaseStatus === PROJECT_RELEASE_STATUS_ENUM.Cancelled;
    }

    public getMigrateLotsDisabledReason(): string | null {
        if (this.isReleaseStageCancelled()) {
            return MIGRATION_RELEASE_CANCELLED_TOOLTIP;
        }

        return null;
    }
}
